/** @jsxImportSource @emotion/react */
import keys from './../../i18n/keys';
import React, { useState, useRef } from 'react';
import { PageCnt } from 'components/Card/PageCnt';
import { WithSidebar } from 'components/Sidebar/Sidebar';
import { SimulatorHeader } from 'components/SimulatorHeader/SimulatorHeader';
import { SForm, SH2, SInput, SLabel, SPageCard, STextarea } from './styles';
import { Button } from 'components/Button/Button';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Footer } from 'components/Footer/Footer';
import { useTitle } from 'react-use';
import ReCAPTCHA from 'react-google-recaptcha';

const cardShell = css`
  display: flex;
  flex-direction: column;
  padding: 40px 28px;
  gap: 24px;
`;

/* Grid 2 kolumny × 2 rzędy:
   r1: [formTitle | .]
   r2: [formFields | imageArea]  <-- obraz centrowany pionowo względem pól
*/
const grid = css`
  display: grid;
  grid-template-columns: minmax(340px, 1fr) minmax(340px, 1fr);
  grid-template-rows: auto auto;
  grid-template-areas:
    'formTitle .'
    'formFields imageArea';
  column-gap: 56px;
  row-gap: 16px;
  align-items: stretch;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
    grid-template-areas:
      'formTitle'
      'formFields'
      'imageArea';
    column-gap: 0;
    row-gap: 24px;
  }
`;

const formTitle = css`
  grid-area: formTitle;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 8px 0;
`;

const formFields = css`
  grid-area: formFields;
  display: flex;
  flex-direction: column;
`;

const field = css`
  margin-bottom: 18px;
`;

const imageArea = css`
  grid-area: imageArea;
  align-self: stretch; /* wypełnij wysokość wiersza 2 (tak jak formFields) */
  display: flex;
  justify-content: center; /* środek poziomo */
  align-items: center; /* ✨ środek pionowo względem wysokości pól */
  min-width: 0;
  height: 100%;

  img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    height: auto;
    object-fit: contain;
  }
`;

/* Pasek sponsorów wewnątrz karty, bez górnej kreski */
const sponsorsBar = css`
  margin-top: auto;
  padding-top: 2px;
`;

export const Contact = () => {
  const { t } = useTranslation();
  useTitle(t(keys.PAGE_TITLE_CONTACT));

  const [isCaptchaAccepted, setIsCaptchaAccepted] = useState(false);
  const [captchaErrored, setCaptchaErrored] = useState(false);
  const captchaRef = useRef<ReCAPTCHA | null>(null);

  const onChange = (value: string | null) => {
    setIsCaptchaAccepted(!!value);
    if (value) setCaptchaErrored(false);
  };

  const onExpired = () => {
    setIsCaptchaAccepted(false);
  };

  const onErrored = () => {
    setCaptchaErrored(true);
    setIsCaptchaAccepted(false);
  };

  const clearForm = () => {
    document.forms[0]?.reset();
    captchaRef.current?.reset?.();
    setIsCaptchaAccepted(false);
    setCaptchaErrored(false);
  };

  // DEV fallback: użyj oficjalnego testowego site key Google, jeśli nie podano własnego
  const TEST_RECAPTCHA_SITE_KEY = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
  const envSiteKey = process.env.REACT_APP_CAPTCHA_KEY as string | undefined;
  const captchaKey = process.env.NODE_ENV === 'production' ? envSiteKey || '' : envSiteKey || TEST_RECAPTCHA_SITE_KEY;

  const isProd = process.env.NODE_ENV === 'production';

  return (
    <div>
      <WithSidebar>
        <PageCnt>
          {/* Nagłówek nad kartą */}
          <SimulatorHeader title={t(keys.PAGE_TITLE_CONTACT)} />

          {/* Karta z gridem i logotypami na dole karty */}
          <SPageCard css={cardShell} className="css-18zu2r6-Contact">
            <div css={grid}>
              {/* r1: tytuł sekcji pól */}
              <SH2 css={formTitle}>{t(keys.SEND_MESSAGE_CREATORS)}</SH2>

              {/* r2: edytowalne pola + reCAPTCHA + przyciski (jeden wspólny flow) */}
              <div css={formFields}>
                <SForm action="https://formspree.io/f/xeqdgzbz" method="POST" noValidate>
                  <div css={field}>
                    <SLabel htmlFor="email">
                      {t(keys.YOUR_EMAIL)}
                      <SInput
                        required
                        id="email"
                        type="email"
                        name="email"
                        placeholder={t(keys.YOUR_EMAIL_PLACEHOLDER)}
                      />
                    </SLabel>
                  </div>

                  <div css={field}>
                    <SLabel htmlFor="message">
                      {t(keys.MESSAGE_BODY)}
                      <STextarea id="message" name="message" placeholder={t(keys.MESSAGE_BODY_PLACEHOLDER)} rows={6} />
                    </SLabel>
                  </div>

                  {/* reCAPTCHA tuż pod textarea */}
                  <div css={field}>
                    <ReCAPTCHA
                      ref={captchaRef as any}
                      sitekey={captchaKey}
                      onChange={onChange}
                      onExpired={onExpired}
                      onErrored={onErrored}
                    />
                    {captchaErrored && isProd && (
                      <p
                        css={css`
                          margin: 8px 0 0;
                          font-size: 12px;
                          color: #c62828;
                        `}
                      >
                        reCAPTCHA nie mogła się załadować. Upewnij się, że domena jest dodana w panelu Google reCAPTCHA
                        (Admin Console) do listy dozwolonych dla tego klucza.
                      </p>
                    )}
                    {!envSiteKey && !isProd && (
                      <p
                        css={css`
                          margin: 8px 0 0;
                          font-size: 12px;
                          opacity: 0.75;
                        `}
                      >
                        Używasz testowego site key Google (DEV). W produkcji ustaw <code>REACT_APP_CAPTCHA_KEY</code> i
                        dodaj domenę w reCAPTCHA Admin Console.
                      </p>
                    )}
                  </div>

                  {/* Akcje */}
                  <div
                    css={css`
                      display: flex;
                      justify-content: center;
                      gap: 10px;
                    `}
                  >
                    <Button dense type="button" onClick={clearForm}>
                      {t(keys.CANCEL)}
                    </Button>
                    <Button variant="outlined" type="submit" dense disabled={!isCaptchaAccepted}>
                      {t(keys.SEND)}
                    </Button>
                  </div>
                </SForm>
              </div>

              {/* r2: obraz – centrowany pionowo względem wysokości pól */}
              <div css={imageArea}>
                <img src="/images/Device - Macbook Air old.png" alt={t(keys.PAGE_TITLE_CONTACT)} />
              </div>
            </div>

            {/* logotypy w ramce, przy dolnej krawędzi ramki */}
            <div css={sponsorsBar}>
              <Footer />
            </div>
          </SPageCard>
        </PageCnt>
      </WithSidebar>
    </div>
  );
};

export default Contact;
